<template>
  <SrpModal :isVisible="props.isVisible" size="large" :isClosable="false" :isCloseButtonVisible="activeForm !== AllForms.SelectHighlights" @update:isVisible="handleModalVisibilityUpdate">
    <template #insideWindow>
      <div v-if="activeForm === AllForms.SelectHighlights" class="modal-close-button edit-share-modal__select-close" @click="goBackToEditAndShare">
        <IconEmbedded class="modal-close-button__icon" name="remove_4-5" />
      </div>
    </template>

    <template #content>
      <EditAndShareForm
        v-if="activeForm === AllForms.EditAndShare"
        ref="editFormRef"
        :restoreEditTarget="activeAssetEditTarget"
        :postToEdit="draftPost"
        :customerId="props.customerId"
        :isLoadingSummaryPayload="isLoadingSummaryPayload"
        :socialShareSummaryPayload="socialShareSummaryPayload"
        :savePostCallback="saveDraft"
        :isSavingPost="isSavingPost"
        :customerSocialChannels="props.customerSocialChannels"
        :readonlyMode="props.readonlyMode"
        :sectionsToHide="props.sectionsToHide"
        :calendarImageFit="props.calendarImageFit"
        @requestDownload="isInProgress => (isRequestingDownload = isInProgress)"
        @clickEditImages="handleClickEditImages"
        @addSocialAccount="emit('addSocialAccount')"
        @update:isCollaboratorValidationBusy="isCollaboratorValidationBusy = $event"
      />

      <PostPhotoSelectionForm
        v-if="activeForm === AllForms.SelectHighlights"
        ref="domRefPostPhotoSelectionForm"
        :customerId="props.customerId"
        :allAssetIds="socialShareSummaryPayload?.assets || []"
        :selectedAssetIds="selectedAssetIdsForEditTarget"
        :title="photosModalTitle"
        :postType="draftPost?.postType ?? SocialPostPublishType.Post"
        @cancel="goBackToEditAndShare"
        @done="
          assetIds => {
            setAssetIdsForTarget(draftPost, activeAssetEditTarget, assetIds);
            goBackToEditAndShare();
          }
        "
      />
    </template>

    <template #footer="{ closeModal }">
      <div style="gap: 12px; display: flex; flex-wrap: wrap">
        <template v-if="activeForm === AllForms.EditAndShare">
          <SrpButton color="gray" fill="outlined" @click="closeModal(false, true)" :isDisabled="areButtonsDisabled">Cancel</SrpButton>

          <SrpButton :isDisabled="areButtonsDisabled || props.readonlyMode || isCollaboratorValidationBusy" @click="async () => await savePostAndClose(closeModal)">
            <template #icon>
              <LoadingIcon v-if="isSavingPost || isCollaboratorValidationBusy" :size="18" />
              <IconEmbedded v-else name="save_2-5" :size="18" />
            </template>
            Save
          </SrpButton>
        </template>

        <template v-if="activeForm === AllForms.SelectHighlights">
          <SrpButton color="gray" fill="outlined" @click="domRefPostPhotoSelectionForm?.cancel" :isDisabled="areButtonsDisabled">Cancel</SrpButton>

          <SrpButton @click="domRefPostPhotoSelectionForm?.done" :isDisabled="areButtonsDisabled || props.readonlyMode">
            <template #icon>
              <LoadingIcon v-if="isSavingPost" :size="18" />
              <IconEmbedded v-else name="save_2-5" :size="18" />
            </template>
            Done
          </SrpButton>
        </template>
      </div>
    </template>
  </SrpModal>
</template>

<script lang="ts">
export enum SocialPostEditAndShareFormSections {
  Caption = "caption",
  PhotosAndVideos = "photos and videos",
  CreatorTag = "creator tag",
}
</script>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import lodashCloneDeep from "lodash-es/cloneDeep";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import { MetricSender } from "@helpers/MetricSender";

// Components
import EditAndShareForm from "./EditAndShareForm.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import PostPhotoSelectionForm from "./PostPhotoSelectionForm.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";

// Types
import { SocialImageFitSettings, SocialMediaPostData, SocialShareSummaryPayload, SocialPostPublishType } from "@contracts/shareStop";
import { getSocialChannelLabel } from "@contracts/socialNetworks";
import { ChannelEditTarget, getAssetIdsForTarget, setAssetIdsForTarget } from "@logic/SocialPostChannelOverrides";
import { getChannelPhotoSelectorTitle, getDefaultPhotoSelectorTitle } from "@logic/AtlasSchedulingValidation";
import type { SocialChannelData } from "@contracts/socialMediaSettings";

defineOptions({
  name: "SocialPostEditAndShareModal",
});

const props = withDefaults(
  defineProps<{
    isVisible?: boolean;
    postToEdit: SocialMediaPostData | null;
    customerId: string;
    customerSocialChannels?: Array<SocialChannelData>;
    readonlyMode?: boolean;
    sectionsToHide?: Array<SocialPostEditAndShareFormSections>;
    sourceEntityName?: string | null;
    calendarImageFit?: SocialImageFitSettings | null;
  }>(),
  {
    isVisible: false,
    postToEdit: null,
    customerId: "",
    customerSocialChannels: () => [],
    readonlyMode: false,
    sectionsToHide: () => [],
    sourceEntityName: null,
    calendarImageFit: null,
  }
);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "savePost", post: SocialMediaPostData): void;
  (e: "addSocialAccount"): void;
}>();

// We now copy into a draft so we're not mutating the original post (in case the user cancels the edit)
const draftPost = ref<SocialMediaPostData | null>(null);

// DOM refs
const editFormRef = ref<{ ensureCollaboratorsValid: () => Promise<boolean> } | null>(null);
const isCollaboratorValidationBusy = ref(false);
const domRefPostPhotoSelectionForm = ref<any>(null);
type EditTarget = ChannelEditTarget;
const activeAssetEditTarget = ref<EditTarget>("template");

// Switch between forms =======================================================
enum AllForms {
  EditAndShare,
  SelectHighlights,
}

const activeForm = ref<AllForms>(AllForms.EditAndShare);

// Is Requesting download =====================================================
const isRequestingDownload = ref<boolean>(false);

// Load socialShareSummaryPayload =============================================
const isLoadingSummaryPayload = ref<boolean>(false);
const socialShareSummaryPayload = ref<SocialShareSummaryPayload | null>(null);

async function loadShareSummaryPayload() {
  const postId = draftPost.value?.id ?? props.postToEdit?.id;
  if (!postId) return;
  isLoadingSummaryPayload.value = true;
  socialShareSummaryPayload.value = await ShareStopSummaryRepo.loadSocialShareSummaryPayloadFromPostId(props.customerId, postId);
  isLoadingSummaryPayload.value = false;
}

watch(
  () => [props.isVisible, props.postToEdit?.id] as const,
  async ([isVisible]) => {
    if (isVisible && props.postToEdit) {
      // See comment on draftPost for why we clone the post
      draftPost.value = lodashCloneDeep(props.postToEdit);
      activeForm.value = AllForms.EditAndShare;
      activeAssetEditTarget.value = "template";
      await loadShareSummaryPayload();
      MetricSender.sendMetric("Cust-SocialPost-Open", {
        socialPostId: props.postToEdit.id,
        aiGen: props.postToEdit.createdWithAI,
        autoGen: props.postToEdit.autoGenerated,
      });
      return;
    }
    if (!isVisible) {
      draftPost.value = null;
    }
  },
  { immediate: true }
);

// Save post ==================================================================
const isSavingPost = ref<boolean>(false);

async function persistDraft(): Promise<SocialMediaPostData | undefined> {
  if (!draftPost.value) return;
  isSavingPost.value = true;
  const updated = await ShareStopSummaryRepo.updatePost(draftPost.value);
  draftPost.value = updated;
  isSavingPost.value = false;
  return updated;
}

/**
 * Persists the modal draft. When mergeToParent is true, also emits savePost so the calendar/list
 * replaces its live post (footer Save, Pintura). When false or omitted, only the API is updated
 * so Cancel still discards in-modal edits on the parent (e.g. add social account before OAuth).
 */
async function saveDraft(options?: { mergeToParent?: boolean }): Promise<SocialMediaPostData | undefined> {
  const collaboratorsOk = await editFormRef.value?.ensureCollaboratorsValid();
  if (collaboratorsOk === false) return;
  const updated = await persistDraft();
  if (updated && options?.mergeToParent) {
    emit("savePost", updated);
  }
  return updated;
}

async function savePostAndClose(closeModal: (isClickOnBgLayer?: boolean, isForce?: boolean) => void) {
  const updated = await saveDraft({ mergeToParent: true });
  if (updated) closeModal();
}

// Are the modal buttons disabled =============================================
const areButtonsDisabled = computed(() => {
  return isLoadingSummaryPayload.value || isSavingPost.value || isRequestingDownload.value;
});

function handleClickEditImages(target: EditTarget) {
  activeAssetEditTarget.value = target;
  activeForm.value = AllForms.SelectHighlights;
}

function handleModalVisibilityUpdate(isVisible: boolean) {
  emit("update:isVisible", isVisible);
}

function goBackToEditAndShare() {
  activeForm.value = AllForms.EditAndShare;
}

const selectedAssetIdsForEditTarget = computed(() => {
  return getAssetIdsForTarget(draftPost.value, activeAssetEditTarget.value);
});

const photosModalTitle = computed(() => {
  const postType = draftPost.value?.postType ?? SocialPostPublishType.Post;
  if (activeAssetEditTarget.value === "template") {
    return props.sourceEntityName ? `Select from "${props.sourceEntityName}"` : getDefaultPhotoSelectorTitle(postType);
  }
  return getChannelPhotoSelectorTitle(postType, getSocialChannelLabel(activeAssetEditTarget.value));
});
</script>

<style scoped lang="scss">
@import "@/scss/modal-close-button.scss";

.edit-share-modal__select-close {
  position: absolute;
  inset: -8px -8px auto auto;
  z-index: 12;
}
</style>
